import cs from 'classnames';
import throttle from 'lodash/throttle';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import InkBar from './components/InkBar';
import ShowMore from './components/ShowMore';
import Tab from './components/Tab';
import TabPanel from './components/TabPanel';

const tabPrefix = 'tab-';
const panelPrefix = 'panel-';

interface TabItem {
	key?: number | string;
	title?: React.ReactNode;
	content?: React.ReactNode;
	getContent?: () => React.ReactNode;
	disabled?: boolean;
	tabClassName?: string;
	panelClassName?: string;
	isComingSoon?: boolean;
	comingSoonTooltip?: React.ReactNode;
}

interface TabsProps {
	items?: TabItem[];
	selectedTabKey?: number | string;
	allowRemove?: boolean;
	removeActiveOnly?: boolean;
	showMore?: boolean;
	showInkBar?: boolean;
	transform?: boolean;
	transformWidth?: number;
	beforeChange?: (params: { selectedTabKey: number | string | null; nextTabKey: number | string }) => boolean | void;
	onChange?: (nextTabKey: number | string) => void;
	onRemove?: (key: number | string, evt: React.MouseEvent) => void;
	resizeThrottle?: number;
	unmountOnExit?: boolean;
	containerClass?: string;
	tabsWrapperClass?: string;
	tabClass?: string;
	panelClass?: string;
	uid?: any;
	showMoreLabel?: string | React.ReactNode;
}

interface TabDimensions {
	[key: string]: {
		width: number;
		offset: number;
	};
}

const Tabs: React.FC<TabsProps> = (props) => {
	const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
	const targetRef = useRef<HTMLDivElement | null>(null);

	const [tabDimensions, setTabDimensions] = useState<TabDimensions>({});
	const [blockWidth, setBlockWidth] = useState(0);
	const [tabsTotalWidth, setTabsTotalWidth] = useState(0);
	const [showMoreWidth, setShowMoreWidth] = useState(40);
	const [selectedTabKey, setSelectedTabKey] = useState<number | string | null>(props.selectedTabKey ?? null);
	const [focusedTabKey, setFocusedTabKey] = useState<number | string | null>(null);

	useEffect(() => {
		setInitialTabsDimensions();
	}, []);

	useEffect(() => {
		if (props.selectedTabKey !== selectedTabKey) {
			// eslint-disable-next-line react/no-did-update-set-state
			setSelectedTabKey(props.selectedTabKey ?? null);
		}
	}, [props.selectedTabKey]);

	useEffect(() => {
		const { items } = props;
		if (selectedTabKey === null && items && items.length) {
			const firstTabKey = items[0].key ?? 0;
			setSelectedTabKey(firstTabKey);
		}
	}, [blockWidth, selectedTabKey, props]);

	const onChangeTab = (nextTabKey: number | string, evt: React.MouseEvent) => {
		const { beforeChange, onChange } = props;
		if (typeof beforeChange === 'function') {
			const beforeChangeRes = beforeChange({ selectedTabKey: selectedTabKey ?? 0, nextTabKey });
			if (beforeChangeRes === false) {
				evt.preventDefault();
				return;
			}
		}

		const getIsCollapsed = () => {
			const { transform, transformWidth = 800 } = props;
			return blockWidth && transform && blockWidth < transformWidth;
		};

		const isCollapsed = getIsCollapsed();
		if (isCollapsed && selectedTabKey === nextTabKey) {
			// hide on mobile
			setSelectedTabKey(-1);
		} else {
			// change active tab
			setSelectedTabKey(nextTabKey);
		}
		if (onChange) {
			onChange(nextTabKey);
		}
	};

	const onFocusTab = (focusedTabKey: number | string) => () => setFocusedTabKey(focusedTabKey);

	const onBlurTab = () => setFocusedTabKey(null);

	const onKeyDown = (event: React.KeyboardEvent) => {
		if (event.keyCode === 13 && focusedTabKey !== null) {
			setFocusedTabKey(focusedTabKey);
		}
	};

	const setInitialTabsDimensions = () => {
		if (!targetRef.current) {
			// it shouldn't happen ever. Just a paranoic check
			return;
		}
		// initial wrapper width calculation;
		const blockWidth = targetRef.current.offsetWidth;
		// calculate width and offset for each tab
		let tabsTotalWidth = 0;
		const tabDimensionsNext: TabDimensions = {};
		Object.keys(tabRefs.current).forEach((key) => {
			if (tabRefs.current[key] && key !== 'current') {
				const tabKey = key.replace(tabPrefix, '');
				const width = tabRefs.current[key]?.offsetWidth ?? 0;
				if (width) {
					tabDimensionsNext[tabKey] = { width, offset: tabsTotalWidth };
				} else {
					tabDimensionsNext[tabKey] = tabDimensions[tabKey];
				}
				tabsTotalWidth += tabDimensionsNext[tabKey].width;
			}
		});
		setTabDimensions(tabDimensionsNext);
		setTabsTotalWidth(tabsTotalWidth);
		setBlockWidth(blockWidth);
	};

	const getTabs = () => {
		const {
			showMore,
			transform,
			transformWidth = 800,
			items = [],
			allowRemove,
			removeActiveOnly,
			onRemove,
		} = props;
		const selectedTabKeyValue = getSelectedTabKey();
		const collapsed = blockWidth && transform && blockWidth < transformWidth;

		let tabIndex = 0;
		let availableWidth =
			blockWidth - (tabsTotalWidth > blockWidth ? showMoreWidth : 0);

		return items.reduce(
			(result, item, index) => {
				const {
					key: itemKey = index,
					title,
					content,
					getContent,
					disabled,
					tabClassName,
					panelClassName,
					isComingSoon,
					comingSoonTooltip,
				} = item;
				const key = itemKey;

				const selected = selectedTabKeyValue === key;
				const payload = { tabIndex, collapsed, selected, disabled, key };
				const tabPayload = {
					...payload,
					title,
					onRemove: (evt: React.MouseEvent) => {
						if (typeof onRemove === 'function') {
							onRemove(key, evt);
						}
					},
					allowRemove: allowRemove && (!removeActiveOnly || selected),
					className: tabClassName,
					isComingSoon,
					comingSoonTooltip,
				};

				const panelPayload = {
					...payload,
					content,
					getContent,
					className: panelClassName,
				};

				const tabWidth = tabDimensions[key] ? tabDimensions[key].width : 0;

				tabIndex += 1;

				/* eslint-disable no-param-reassign */
				if (
					// don't need to `Show more` button
					!showMore ||
					// initial call
					!blockWidth ||
					// collapsed mode
					collapsed ||
					// all tabs are fit into the block
					blockWidth > tabsTotalWidth ||
					// current tab fit into the block
					availableWidth - tabWidth > 0
				) {
					tabPayload.className =
						`${tabPayload.className} ` + 'RRT__visible-tabs';
					result.tabsVisible.push(tabPayload);
				} else {
					tabPayload.className =
						`${tabPayload.className} ` + 'RRT__hidden-tabs';
					result.tabsHidden.push(tabPayload);
					if (selected) result.isSelectedTabHidden = true;
				}
				/* eslint-enable no-param-reassign */

				result.panels[key] = panelPayload; // eslint-disable-line no-param-reassign
				availableWidth -= tabWidth;

				return result;
			},
			{
				tabsVisible: [] as any[],
				tabsHidden: [] as any[],
				panels: {} as any,
				isSelectedTabHidden: false,
			},
		);
	};

	const getTabProps = ({
		title,
		key,
		selected,
		collapsed,
		tabIndex,
		disabled,
		className,
		onRemove,
		allowRemove,
		isComingSoon,
		comingSoonTooltip,
	}: any) => ({
		selected,
		allowRemove,
		children: title,
		//key: tabPrefix + key,
		id: tabPrefix + key,
		ref: (e: HTMLDivElement | null) => {
			tabRefs.current[tabPrefix + key] = e;
		},
		originalKey: key,
		onClick: onChangeTab,
		onFocus: onFocusTab,
		onBlur: onBlurTab,
		onRemove,
		panelId: panelPrefix + key,
		classNames: getClassNamesFor('tab', {
			selected,
			collapsed,
			tabIndex,
			disabled,
			className,
		}),
		disabled,
		isComingSoon, //check
		comingSoonTooltip,
	});

	const getPanelProps = (
		{ key, content, getContent, className }: any,
		isHidden: boolean,
	) => ({
		getContent,
		children: content,
		//key: panelPrefix + key,
		id: panelPrefix + key,
		tabId: tabPrefix + key,
		classNames: getClassNamesFor('panel', { className, isHidden }),
		isHidden,
	});

	const getShowMoreProps = (isShown?: boolean, isSelectedTabHidden?: boolean, showMoreLabel?: string | React.ReactNode) => ({
		onShowMoreChanged: showMoreChanged,
		isShown,
		label: showMoreLabel,
		hasChildSelected: isSelectedTabHidden,
	});

	const getClassNamesFor = (
		type: 'tab' | 'panel',
		{ selected, collapsed, tabIndex, disabled, className = '', isHidden }: any,
	) => {
		const { tabClass, panelClass } = props;
		switch (type) {
		case 'tab':
			return cs('RRT__tab', className, tabClass, {
				'RRT__tab--first': !tabIndex,
				'RRT__tab--selected': selected,
				'RRT__tab--disabled': disabled,
				'RRT__tab--collapsed': collapsed,
			});
		case 'panel':
			return cs('RRT__panel', className, panelClass, {
				'RRT__panel--hidden': isHidden,
			});
		default:
			return '';
		}
	};

	const getSelectedTabKey = (): number | string => {
		const { items = [] } = props;
		if (typeof selectedTabKey === 'undefined' || selectedTabKey === null) {
			if (!items[0]) {
				return 0;
			}
			return items[0].key ?? 0;
		}
		return selectedTabKey;
	};

	const getIsCollapsed = () => {
		const { transform, transformWidth = 800 } = props;
		return blockWidth && transform && blockWidth < transformWidth;
	};

	const showMoreChanged = (element: HTMLDivElement | null) => {
		if (!element) {
			return;
		}
		const { offsetWidth } = element;
		if (showMoreWidth === offsetWidth) {
			return;
		}
		setShowMoreWidth(offsetWidth);
	};

	const getExpandedTabs = (panels: any, selectedTabKey: number | string, isCollapsed: boolean) => {
		const { unmountOnExit } = props;
		if (isCollapsed) {
			return undefined;
		}

		if (!unmountOnExit) {
			// render all tabs if unmountOnExit === false (inactive are hidden)
			return Object.keys(panels).map((key) => (
				<TabPanel
					key={`expanded-panel-${key}`}
					{...getPanelProps(panels[key], `${selectedTabKey}` !== `${key}`)}
				/>
			));
		}

		if (panels[selectedTabKey]) {
			// render only active tab if unmountOnExit === true
			return <TabPanel key={`active-panel-${selectedTabKey}`} {...getPanelProps(panels[selectedTabKey], false)} />;
		}

		return undefined;
	};

	const onResize = useCallback(() => {
		if (targetRef.current) {
			setBlockWidth(targetRef.current.offsetWidth);
		}
	}, []);

	const onResizeThrottled = throttle(onResize, props.resizeThrottle ?? 100, {
		trailing: true,
	});
	const {
		showInkBar,
		containerClass,
		tabsWrapperClass,
		showMore,
		transform,
		showMoreLabel,
		unmountOnExit,
	} = props;
	const { tabsVisible, tabsHidden, panels, isSelectedTabHidden } = getTabs();
	const isCollapsed = getIsCollapsed();
	const selectedTabKeyValue = getSelectedTabKey();
	const selectedTabDimensions = tabDimensions[selectedTabKeyValue] || { width: 0, offset: 0 };
	const containerClasses = cs('RRT__container', containerClass);
	const tabsClasses = cs('RRT__tabs', tabsWrapperClass, {
		RRT__accordion: isCollapsed,
	});
	const handleResize = showMore || transform;

	useResizeDetector({ targetRef, onResize: onResizeThrottled });

	return (
		<div
			ref={targetRef}
			className={containerClasses}
			onKeyDown={onKeyDown}
		>
			<div className={tabsClasses}>
				{tabsVisible.reduce((result: React.ReactNode[], tab: any) => {
					result.push(<Tab key={getTabProps(tab).id} {...getTabProps(tab)} />);

					if (
						isCollapsed &&
						(!unmountOnExit || selectedTabKeyValue === tab.key)
					) {
						result.push(
							<TabPanel
								key={`collapsed-panel-${tab.key}`}
								{...getPanelProps(
									panels[tab.key],
									selectedTabKeyValue !== tab.key,
								)}
							/>,
						);
					}
					return result;
				}, [])}

				{!isCollapsed && (
					<ShowMore
						{...getShowMoreProps(showMore, isSelectedTabHidden, showMoreLabel)}
					>
						{tabsHidden.map((tab: any) => (
							<Tab key={getTabProps(tab).id} {...getTabProps(tab)} />
						))}
					</ShowMore>
				)}
			</div>
			{showInkBar && !isCollapsed && !isSelectedTabHidden && (
				<InkBar
					left={selectedTabDimensions.offset || 0}
					width={selectedTabDimensions.width || 0}
				/>
			)}
			{getExpandedTabs(panels, selectedTabKey ?? 0, isCollapsed)}
		</div>
	);
};


Tabs.displayName = 'Tabs';
export default Tabs;
